import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { InputSelectData, ValidatorGenerator } from '@pt/pt-shared';
import {
  AttachFileDialogResponse,
  description_min_rows,
  REQUIRED_VALIDATOR_KEY,
  RiskEvaluationLevelEnums,
  SimplifiedAppAssessment
} from '@pt/pt-models';
import { TranslateService } from '@ngx-translate/core';
import {
  GetApplicationForDetailsResponse
} from 'it/primatoo/openapi/api/org/schemas/application/getapplicationfordetailsresponse/GetApplicationForDetailsResponse';
import { Attachment } from 'it/primatoo/openapi/api/org/schemas/common/Attachment';

@Component({
  selector: 'pt-create-simplified-app-assessment-dialog',
  templateUrl: './create-simplified-app-assessment-dialog.component.html',
  styles: [`
    pt-input-select {
      width: 150px;
    }
  `]
})
export class CreateSimplifiedAppAssessmentDialogComponent implements OnInit, OnDestroy {

  @Input() dialogMode: 'CREATE' | 'EDIT' = 'CREATE';

  @Input() application: GetApplicationForDetailsResponse;
  @Input() simplifiedAppAssessment: SimplifiedAppAssessment;

  public simplified = true;
  public formGroup: FormGroup;
  public showAttachError = false;
  public descriptionMinRows = description_min_rows;
  public REQUIRED = ValidatorGenerator.generateWidgetValidator(REQUIRED_VALIDATOR_KEY);

  public inputRiskEvaluationLevelSelectOptions: InputSelectData[] = [
    {
      value: RiskEvaluationLevelEnums.VERY_HIGH,
      label: this.translateService.stream('RISK_EVALUATION_OPTIONS.VERY_HIGH')
    },
    {
      value: RiskEvaluationLevelEnums.HIGH,
      label: this.translateService.stream('RISK_EVALUATION_OPTIONS.HIGH')
    },
    {
      value: RiskEvaluationLevelEnums.MEDIUM,
      label: this.translateService.stream('RISK_EVALUATION_OPTIONS.MEDIUM')
    },
    {
      value: RiskEvaluationLevelEnums.LOW,
      label: this.translateService.stream('RISK_EVALUATION_OPTIONS.LOW')
    },
    {
      value: RiskEvaluationLevelEnums.VERY_LOW,
      label: this.translateService.stream('RISK_EVALUATION_OPTIONS.VERY_LOW')
    }
  ];

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    public activeModal: NgbActiveModal,
    private translateService: TranslateService
  ) {
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  ngOnInit(): void {
    switch (this.dialogMode) {
      case 'CREATE':
        if (this.application.assessmentCategory === 'PRIMATOO') {
          this.simplified = false;
          this.formGroup = new FormGroup({
            category: new FormControl(this.application.assessmentCategory),
            description: new FormControl(null),
            application: new FormControl({value: this.application.name ? this.application.name : null, disabled: true}),
            name: new FormControl(this.application.name ? this.application.name : null, [Validators.required]),
          });
        } else {
          this.simplified = true;
          this.formGroup = new FormGroup({
            category: new FormControl(this.application.assessmentCategory),
            description: new FormControl(null),
            attachments: new FormControl(null),
            application: new FormControl({value: this.application.name ? this.application.name : null, disabled: true}),
            name: new FormControl(this.application.name ? this.application.name : null, [Validators.required]),
            r: new FormControl(RiskEvaluationLevelEnums.VERY_HIGH, [Validators.required]),
            i: new FormControl(RiskEvaluationLevelEnums.VERY_HIGH, [Validators.required]),
            d: new FormControl(RiskEvaluationLevelEnums.VERY_HIGH, [Validators.required])
          });
        }
        break;
      case 'EDIT':
        this.formGroup = new FormGroup({
          category: new FormControl(this.simplifiedAppAssessment.category),
          application: new FormControl({value: this.application?.name ? this.application.name : null, disabled: true}),
          description: new FormControl(this.simplifiedAppAssessment?.description ? this.simplifiedAppAssessment.description : null),
          attachments: new FormControl(this.simplifiedAppAssessment?.attachments ? this.simplifiedAppAssessment.attachments : null),
          name: new FormControl(this.simplifiedAppAssessment?.name ? this.simplifiedAppAssessment.name : null, [Validators.required]),
          r: new FormControl(this.simplifiedAppAssessment?.r, [Validators.required]),
          i: new FormControl(this.simplifiedAppAssessment?.i, [Validators.required]),
          d: new FormControl(this.simplifiedAppAssessment?.d, [Validators.required])
        });
        break;
    }
  }


  confirm() {
    this.activeModal.close(
      this.formGroup.getRawValue()
    );
  }

  onAttachAction($event: AttachFileDialogResponse) {
    const updateAttachments: Attachment[] =
      this.formGroup.controls?.attachments?.value ?
        this.formGroup.controls?.attachments?.value : [];

    this.showAttachError = false;
    updateAttachments.push($event.data);
    this.formGroup.controls.attachments.setValue(updateAttachments);
    this.formGroup.markAsTouched();
  }

  onDeleteAttachment(attachment: Attachment) {
    const filteredAttachments: Attachment[] = this.formGroup.controls.attachments.value;
    this.formGroup.controls.attachments.setValue(filteredAttachments.filter(a => a.id !== attachment.id));
    this.formGroup.markAsTouched();
  }
}
